import React, { useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson, fetchCurrentUser } from '../utils/authClient.js';
import { localizePath } from '../utils/localizedRoutes.js';
import { AdminPageHead, AdminSection } from '../components/AdminNav.jsx';
import { ActivityLog, AdminLink, FactGrid, McpServerTable, OrchestratorRuntime, ToolTable, UsageLedgerPanel, OpenRouterPanel, displayModelName, formatBerlin } from './VeridionShared.jsx';
import VeridionCorePanel from './VeridionCorePanel.jsx';

const ACTIVITY_LIMIT = 120;

const errorMessageMap = {
  VERIDION_UNAVAILABLE: 'veridionUnavailable',
  VERIDION_NOT_CONFIGURED: 'veridionNotConfigured',
  VERIDION_REQUEST_FAILED: 'veridionRequestFailed',
  WEBEX_BRIDGE_NOT_CONFIGURED: 'companyWebexNotConfigured',
  COMPANY_RAG_NOT_CONFIGURED: 'companyKnowledgeNotConfigured',
};

// The bridge's refusals that an admin can act on, each with its own sentence.
const webexErrorMap = {
  RECIPIENT_NOT_ALLOWED: 'companyErrorRecipientNotAllowed',
  DOMAIN_ALREADY_ALLOWED: 'companyErrorDomainAllowed',
  INVALID_EMAIL: 'companyErrorInvalidEmail',
  ALREADY_LISTED: 'companyErrorAlreadyListed',
  STORE_UNWRITABLE: 'companyErrorStoreUnwritable',
};

const webexErrorText = (t, error) => t.admin[webexErrorMap[error?.code]]
  || `${t.admin.companyWebexSendFailed} (${error?.code || error?.status || '-'})`;

const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());

// Sending as the bot from the dashboard: to a person by mail address or into one of the bot's
// rooms. Only a person who may answer the bot can be written to. The admin API records the
// sending admin with every message.
const WebexSendPanel = ({ t, rooms, onSent }) => {
  const [target, setTarget] = useState('person');
  const [email, setEmail] = useState('');
  const [roomId, setRoomId] = useState('');
  const [text, setText] = useState('');
  const [state, setState] = useState({ busy: false, message: '', error: false });

  const send = async (event) => {
    event.preventDefault();
    setState({ busy: true, message: '', error: false });
    try {
      await requestJson('/api/admin/veridion-paioneer/webex/send', {
        method: 'POST',
        body: JSON.stringify(target === 'person' ? { toPersonEmail: email, markdown: text } : { roomId, markdown: text }),
      });
      setText('');
      setState({ busy: false, message: t.admin.companyWebexSendDone, error: false });
      onSent();
    } catch (error) {
      setState({ busy: false, message: webexErrorText(t, error), error: true });
    }
  };

  return (
    <form className="space-y-3" onSubmit={send}>
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2"><input type="radio" checked={target === 'person'} onChange={() => setTarget('person')} />{t.admin.companyWebexSendPerson}</label>
        <label className="flex items-center gap-2"><input type="radio" checked={target === 'room'} onChange={() => setTarget('room')} disabled={!rooms.length} />{t.admin.companyWebexSendRoom}</label>
      </div>
      {target === 'person' ? (
        <input className="form-input w-full" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t.admin.companyWebexSendEmail} />
      ) : (
        <select className="form-input w-full" required value={roomId} onChange={(event) => setRoomId(event.target.value)}>
          <option value="">{t.admin.companyWebexSendRoom}</option>
          {rooms.map((room) => <option key={room.id} value={room.id}>{room.title}</option>)}
        </select>
      )}
      <textarea className="form-input w-full" rows={4} required maxLength={7000} value={text} onChange={(event) => setText(event.target.value)} placeholder={t.admin.companyWebexSendText} />
      <div className="flex flex-wrap items-center gap-3">
        <button className="homepage-button-primary" type="submit" disabled={state.busy}>{t.admin.companyWebexSendButton}</button>
        {state.message ? <span className={`text-sm ${state.error ? 'text-error' : 'text-text-secondary'}`}>{state.message}</span> : null}
      </div>
    </form>
  );
};

// The outside addresses that may use the bot beside the company's mail domain. The bridge keeps
// the list and records which admin changed it; a change applies to the next message.
const GuestAccessPanel = ({ t, access, onChanged, formatTimestamp }) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ busy: false, message: '', error: false });
  const guests = access?.guests || [];
  const domains = (access?.emailDomains || []).map((domain) => `@${domain}`).join(', ') || '-';

  const change = async (path, address) => {
    setState({ busy: true, message: '', error: false });
    try {
      await requestJson(path, { method: 'POST', body: JSON.stringify({ email: address }) });
      setState({ busy: false, message: '', error: false });
      return true;
    } catch (error) {
      setState({ busy: false, message: webexErrorText(t, error), error: true });
      return false;
    } finally {
      onChanged();
    }
  };

  const add = async (event) => {
    event.preventDefault();
    if (await change('/api/admin/veridion-paioneer/webex/access/guests', email)) {
      setEmail('');
    }
  };

  const remove = (guest) => {
    if (window.confirm(t.admin.companyGuestsRemoveConfirm.replace('{email}', guest.email))) {
      change('/api/admin/veridion-paioneer/webex/access/guests/remove', guest.email);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">{t.admin.companyGuestsHint.replace('{domains}', domains)}</p>
      {access && !access.guestListPersistent ? <div className="text-sm text-error">{t.admin.companyGuestsNotPersistent}</div> : null}
      {access?.guestListLoadFailed ? <div className="text-sm text-error">{t.admin.companyGuestsLoadFailed}</div> : null}
      <form className="admin-form-row" onSubmit={add}>
        <input className="form-input" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t.admin.companyGuestsEmail} />
        <button className="homepage-button-primary" type="submit" disabled={state.busy}>{t.admin.companyGuestsAdd}</button>
        {state.message ? <span className={`text-sm ${state.error ? 'text-error' : 'text-text-secondary'}`}>{state.message}</span> : null}
      </form>
      <div className="admin-table-shell">
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t.admin.companyGuestsEmail}</th>
                <th>{t.admin.companyGuestsAddedBy}</th>
                <th>{t.admin.companyGuestsAddedAt}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {guests.map((guest) => (
                <tr key={guest.email}>
                  <td className="break-all">{guest.email}</td>
                  <td className="break-all">{guest.addedBy || '-'}</td>
                  <td>{formatTimestamp(guest.addedAt)}</td>
                  <td><button type="button" className="homepage-button-secondary is-destructive" disabled={state.busy} onClick={() => remove(guest)}>{t.admin.companyGuestsRemove}</button></td>
                </tr>
              ))}
              {!guests.length ? <tr><td colSpan="4">{t.admin.companyGuestsEmpty}</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// The company knowledge base: what it holds, adding a document and removing one. The intake runs
// in the RAG's background; the page shows its state on the next refresh.
const KnowledgePanel = ({ t, knowledge, onChanged, formatTimestamp }) => {
  const [state, setState] = useState({ busy: false, message: '', error: false });
  const documents = knowledge?.documents || [];

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setState({ busy: true, message: t.admin.companyKnowledgeUploading, error: false });
    const form = new FormData();
    form.append('file', file);
    form.append('training_artifacts', 'false');
    try {
      const response = await fetch('/api/admin/veridion-paioneer/knowledge/upload', { method: 'POST', body: form, credentials: 'include' });
      if (!response.ok) {
        throw new Error(String(response.status));
      }
      setState({ busy: false, message: '', error: false });
      onChanged();
    } catch (error) {
      setState({ busy: false, message: `${t.admin.companyWebexSendFailed} (${error.message})`, error: true });
    }
  };

  const remove = async (document) => {
    if (!window.confirm(t.admin.companyKnowledgeDeleteConfirm.replace('{name}', document.name))) {
      return;
    }
    try {
      await requestJson('/api/admin/veridion-paioneer/knowledge/delete', { method: 'POST', body: JSON.stringify({ path: document.path }) });
      onChanged();
    } catch (error) {
      setState({ busy: false, message: `${t.admin.companyWebexSendFailed} (${error.code || error.status || '-'})`, error: true });
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <label className="homepage-button-primary cursor-pointer">
          {t.admin.companyKnowledgeUpload}
          <input type="file" accept=".pdf,.docx,.doc,.txt,.md,.markdown" className="hidden" onChange={upload} disabled={state.busy} />
        </label>
        {knowledge?.phoenix ? <a className="homepage-button-secondary" href={knowledge.phoenix} target="_blank" rel="noreferrer">{t.admin.companyKnowledgePhoenix}</a> : null}
        <span className="text-sm text-text-secondary">{t.admin.companyKnowledgeIntake}: {knowledge?.intake?.status || '-'}{knowledge?.intake?.files_pending ? ` (${knowledge.intake.files_pending})` : ''}</span>
        {state.message ? <span className={`text-sm ${state.error ? 'text-error' : 'text-text-secondary'}`}>{state.message}</span> : null}
      </div>
      <div className="admin-table-shell">
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t.admin.companyKnowledgeDocument}</th>
                <th>{t.admin.companyKnowledgeSize}</th>
                <th>{t.admin.companyKnowledgeModified}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {documents.map((document) => (
                <tr key={document.path}>
                  <td className="break-all">{document.name}</td>
                  <td>{Math.max(1, Math.round((document.size_bytes || 0) / 1024)).toLocaleString()} KB</td>
                  <td>{formatTimestamp(document.modified_at)}</td>
                  <td><button type="button" className="homepage-button-secondary is-destructive" onClick={() => remove(document)}>{t.admin.companyKnowledgeDelete}</button></td>
                </tr>
              ))}
              {!documents.length ? <tr><td colSpan="4">{t.admin.companyKnowledgeEmpty}</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

const humanizeToken = (value) => String(value || '-')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

// Veridion pAIoneer Labs GmbH: the company's own instance with its Webex bridge. One overview
// request carries every part from the runtime that owns it; a part that fails shows its own
// error and leaves the others visible. In the dashboard the page shows one view at a time (IDD
// D-HP-ADMIN-040), each on the page of the working day it belongs to: `orchestrator` (with its
// sections runtime, mcp, tools, activity), `core`, `agents`, `designs`, `definitions`, `builds`,
// `organisation` (with its sub-page in `section`), `webex`, `knowledge` and `usage`. Alone, the
// page shows every view in turn.
const VeridionPaioneerPage = ({ embedded = false, view = '', section = '' }) => {
  const { t, currentLang } = useTranslation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [activity, setActivity] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tools, setTools] = useState([]);

  const formatTimestamp = formatBerlin;
  const mapError = (value) => (value ? (t.admin?.[errorMessageMap[value.split(/[_ ]\d{3}$/)[0]] || errorMessageMap[value]] || t.admin.companyPartUnavailable) : '');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const currentUser = await fetchCurrentUser();
        setMe(currentUser.user);
        if (!currentUser.user?.roles?.includes('admin')) {
          setLoading(false);
          return;
        }

        const [overviewResult, activityResult, roomsResult, toolsResult] = await Promise.allSettled([
          requestJson('/api/admin/veridion-paioneer/overview'),
          requestJson(`/api/admin/veridion-paioneer/activity?limit=${ACTIVITY_LIMIT}`),
          requestJson('/api/admin/veridion-paioneer/webex/rooms'),
          requestJson('/api/admin/veridion-paioneer/tools'),
        ]);
        setRooms(roomsResult.status === 'fulfilled' ? (roomsResult.value.items || []) : []);
        setTools(toolsResult.status === 'fulfilled' ? (toolsResult.value.items || []) : []);
        if (overviewResult.status === 'fulfilled') {
          setOverview(overviewResult.value);
          setError('');
        } else {
          setError(overviewResult.reason?.code || 'VERIDION_UNAVAILABLE');
        }
        setActivity(activityResult.status === 'fulfilled' ? (activityResult.value.items || []) : []);
      } catch (requestError) {
        setError(requestError.code || 'VERIDION_UNAVAILABLE');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (!me?.roles?.includes('admin')) {
      return undefined;
    }

    let cancelled = false;
    const refresh = async () => {
      try {
        const [nextOverview, nextActivity] = await Promise.all([
          requestJson('/api/admin/veridion-paioneer/overview'),
          requestJson(`/api/admin/veridion-paioneer/activity?limit=${ACTIVITY_LIMIT}`),
        ]);
        if (!cancelled) {
          setOverview(nextOverview);
          setActivity(nextActivity.items || []);
        }
      } catch {
        // Keep the last visible snapshot if a refresh fails.
      }
    };

    const intervalId = window.setInterval(refresh, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [me]);

  const reloadOverview = async () => {
    try {
      setOverview(await requestJson('/api/admin/veridion-paioneer/overview'));
    } catch {
      // The periodic refresh tries again.
    }
  };

  if (loading) {
    return <div className="site-card p-6">{t.auth.validating}</div>;
  }

  if (!me || !me.roles.includes('admin')) {
    return <main className="site-main"><div className="site-card p-6">{t.auth.errors.adminRequired}</div></main>;
  }

  const health = overview?.health?.data;
  const servers = overview?.mcp_servers?.data?.items || [];
  const usage = overview?.usage?.data;
  const gateway = overview?.gateway?.data;
  const coreGateway = overview?.core_gateway?.data;
  const webex = overview?.webex?.data;
  const knowledge = overview?.knowledge?.data;
  const workloads = overview?.workloads?.data || [];
  const llm = health?.llm;
  const useLocalModel = llm?.useLocalModel ?? false;
  const providerLabel = llm?.providerKind || (useLocalModel ? 'local' : '-');
  const modelLabel = displayModelName(llm?.loaded?.model || llm?.loadedModel || llm?.defaultModel) || '-';
  const readyWorkloads = workloads.filter((workload) => workload.state === 'ready').length;
  const counters = webex?.counters || {};
  const n = t.admin.nav || {};
  const o = t.admin.ops || {};
  const budgetOf = (entry) => (!entry ? '-' : entry.unlimited ? t.admin.aiBudgetUnlimited : entry.limit
    ? `${formatInteger(entry.limit)} / ${entry.window || '-'}${entry.budget_is_default ? ` (${t.admin.companyBudgetDefault})` : ''}`
    : '-');
  const connectedServers = servers.filter((server) => server.status === 'connected').length;
  const registeredBots = (webex?.bots || []).filter((bot) => bot.webhook?.registered).length;

  const partError = (part) => (overview?.[part]?.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{mapError(overview[part].error)}</div> : null);

  // In the dashboard one view is on the screen; alone, every view in turn.
  const show = (name) => !embedded || view === name;
  const showSection = (name) => !embedded || !section || section === name;
  // The services the bridge connects, as its status reports them: its list of services when it sends one,
  // otherwise every part of its status that says whether it is connected. The label is the service's own
  // name, else the translated label of the bridge's key, else the key itself.
  const bridgeServices = Array.isArray(webex?.services)
    ? webex.services.map((service) => [service.name || o.bridgeServices?.[service.key] || humanizeToken(service.key), service.connected])
    : Object.entries(webex || {})
      .filter(([, value]) => value && typeof value === 'object' && !Array.isArray(value) && typeof value.connected === 'boolean')
      .map(([key, value]) => [value.name || o.bridgeServices?.[key] || humanizeToken(key), value.connected]);

  const corePanel = (coreView, extra = {}) => (
    <VeridionCorePanel t={t} core={overview?.core?.data} coreGateway={coreGateway} coreError={overview?.core?.error} view={coreView} {...extra} />
  );

  const pageBody = (
    <>
      {show('orchestrator') ? (
        <AdminPageHead
          title={t.admin.companyTitle}
          subtitle={t.admin.companySubtitle}
          meta={[
            [t.admin.veridionCoreStatus, health?.status || '-'],
            [t.admin.veridionProvider, `${providerLabel} / ${modelLabel}`],
            [t.admin.companyWorkloads, `${readyWorkloads} / ${workloads.length}`],
            [t.admin.veridionRegistryReloaded, formatTimestamp(health?.lastRegistryReload)],
          ]}
          actions={(
            <>
              {!embedded ? <a className="homepage-button-secondary" href={localizePath('/admin', currentLang)}>{t.nav?.admin}</a> : null}
              <a className="homepage-button-secondary" href="/api/admin/veridion-paioneer/docs" target="_blank" rel="noreferrer">{t.admin.veridionOpenDocs}</a>
              <AdminLink tab="workloads">{o.workloadsTitle}</AdminLink>
              <AdminLink tab="engines">{t.admin.aiGatewayEngines}</AdminLink>
            </>
          )}
        >
          {error ? <div className="homepage-muted-panel p-4 text-sm text-error">{mapError(error)}</div> : null}
          {partError('health')}
        </AdminPageHead>
      ) : null}

      {show('orchestrator') && showSection('runtime') ? (
        <AdminSection id="company-runtime" title={o.runtimeTitle} subtitle={o.runtimeSubtitle} state={health?.status || '-'} tone={health?.status === 'ok' ? 'ok' : 'warning'}>
          <OrchestratorRuntime health={health} t={t} />
        </AdminSection>
      ) : null}

      {show('orchestrator') && showSection('mcp') ? (
        <AdminSection id="company-mcp" title={t.admin.veridionMcpTitle} subtitle={t.admin.veridionMcpSubtitle}
          state={(n.connectedOf || '').replace('{connected}', connectedServers).replace('{total}', servers.length)}
          tone={servers.some((server) => server.status === 'unreachable') ? 'error' : servers.length ? 'ok' : ''}>
          {partError('mcp_servers')}
          <McpServerTable servers={servers} t={t} formatTimestamp={formatTimestamp} />
        </AdminSection>
      ) : null}

      {show('orchestrator') && showSection('tools') ? (
        <AdminSection id="company-tools" title={o.instanceToolsTitle} subtitle={o.instanceToolsSubtitle} count={tools.length}>
          <ToolTable tools={tools} t={t} />
        </AdminSection>
      ) : null}

      {show('orchestrator') && showSection('activity') ? (
        <>
          <AdminSection id="company-activity" title={t.admin.veridionActivityTitle} subtitle={t.admin.veridionActivitySubtitle} count={activity.length}>
            <ActivityLog activity={activity} t={t} formatTimestamp={formatTimestamp} />
          </AdminSection>
          {corePanel('activity')}
        </>
      ) : null}

      {show('core') ? corePanel('status') : null}
      {show('agents') ? corePanel('agents') : null}
      {show('designs') ? corePanel('designs') : null}
      {show('definitions') ? corePanel('definitions') : null}
      {show('builds') ? corePanel('builds') : null}
      {show('organisation') ? corePanel('organisation', { company: overview, section }) : null}

      {show('webex') ? (
        <>
          <AdminSection id="webex-bots" title={o.botsTitle} subtitle={o.botsSubtitle}
            state={(n.registeredOf || '').replace('{registered}', registeredBots).replace('{total}', (webex?.bots || []).length)}
            tone={(webex?.bots || []).some((bot) => !bot.webhook?.registered) ? 'warning' : (webex?.bots || []).length ? 'ok' : ''}>
            {partError('webex')}
            <div className="admin-table-shell">
              <div className="admin-table-scroll">
                <table className="admin-table admin-fixed-table">
                  <colgroup><col style={{ width: '24%' }} /><col style={{ width: '24%' }} /><col style={{ width: '14%' }} /><col style={{ width: '14%' }} /><col style={{ width: '24%' }} /></colgroup>
                  <thead><tr><th>{t.admin.companyWebexBot}</th><th>{o.botAddress}</th><th>{t.admin.companyWebexWebhook}</th><th>{o.cardWebhook}</th><th>{o.webhookState}</th></tr></thead>
                  <tbody>
                    {(webex?.bots || []).map((bot) => (
                      <tr key={bot.key}>
                        <td><div className="font-semibold text-text-primary">{bot.displayName || bot.key}</div><div className="text-xs text-text-muted">{bot.key}</div></td>
                        <td className="admin-cell-clip" title={bot.email || ''}>{bot.email || '-'}</td>
                        <td><span className={`admin-status-pill ${bot.webhook?.registered ? 'is-ok' : 'is-missing'}`}>{bot.webhook?.registered ? t.admin.companyWebexRegistered : t.admin.companyWebexNotRegistered}</span></td>
                        <td>{bot.webhook?.actions ? t.admin.companyWebexRegistered : t.admin.companyWebexNotRegistered}</td>
                        <td className={bot.webhook?.lastError ? 'text-error' : ''}>{bot.webhook?.lastError ? humanizeToken(bot.webhook.lastError) : (bot.webhook?.status || '-')}</td>
                      </tr>
                    ))}
                    {!(webex?.bots || []).length ? <tr><td colSpan="5">{o.noBots}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </div>
            <FactGrid items={[
              [t.admin.companyWebexSubtitle ? t.admin.companyWebexWebhook : '', webex?.webhook?.targetUrl || '-'],
              [o.webhookState, webex?.webhook?.lastError ? humanizeToken(webex.webhook.lastError) : (webex?.webhook?.status || '-')],
              ...bridgeServices.map(([label, connected]) => [label, webex ? (connected ? o.connected : o.notConnected) : null]),
              [o.activeRooms, webex ? formatInteger(webex.activeRooms) : null],
              [o.allowedRooms, webex ? formatInteger(webex.access?.roomCount) : null],
              [o.surface, webex?.surface],
              [o.missingSettings, webex ? ((webex.missingSettings || []).join(', ') || o.none) : null],
              [t.admin.companyWebexAccess, [...(webex?.access?.emailDomains || []).map((domain) => `@${domain}`), ...(webex?.access?.guests || []).map((guest) => guest.email)].join(', ') || '-'],
              [t.admin.companyWebexAnswered, formatInteger(counters.answered || 0)],
              [t.admin.companyWebexRejected, formatInteger(counters.rejected || 0)],
              [t.admin.companyWebexFailed, formatInteger(counters.failed || 0)],
            ]} />
            <p className="text-xs text-text-muted">{t.admin.companyWebexSubtitle}</p>
          </AdminSection>

          <AdminSection id="webex-recent" title={t.admin.companyWebexRecent} count={(webex?.recent || []).length}>
            <div className="admin-table-shell">
              <div className="admin-table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{t.admin.companyWebexTime}</th>
                      <th>{t.admin.companyWebexOutcome}</th>
                      <th>{t.admin.companyWebexReason}</th>
                      <th>{t.admin.companyWebexSender}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(webex?.recent || []).map((event, index) => (
                      <tr key={`${event.at}-${index}`}>
                        <td>{formatTimestamp(event.at)}</td>
                        <td className={event.kind === 'rejected' || event.kind === 'failed' ? 'text-error' : ''}>{humanizeToken(event.kind)}</td>
                        <td>{humanizeToken(event.reason)}</td>
                        <td className="break-all">{event.personEmail || '-'}</td>
                      </tr>
                    ))}
                    {!(webex?.recent || []).length ? <tr><td colSpan="4">{t.admin.companyWebexEmpty}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </div>
          </AdminSection>

          <AdminSection id="webex-send" title={t.admin.companyWebexSendTitle} collapsed>
            <WebexSendPanel t={t} rooms={rooms} onSent={reloadOverview} />
          </AdminSection>

          <AdminSection id="webex-rooms" title={t.admin.companyWebexRooms} count={rooms.length} collapsed>
            <ul className="admin-rows">
              {rooms.length ? rooms.map((room) => (
                <li key={room.id} className="admin-row"><span>{room.title} · {room.type === 'group' ? t.admin.companyWebexRoomGroup : t.admin.companyWebexRoomDirect} · {formatTimestamp(room.lastActivity)}</span></li>
              )) : <li className="admin-row">{t.admin.companyWebexRoomsEmpty}</li>}
            </ul>
          </AdminSection>

          <AdminSection id="webex-guests" title={t.admin.companyGuestsTitle} count={(webex?.access?.guests || []).length} collapsed>
            <GuestAccessPanel t={t} access={webex?.access} onChanged={reloadOverview} formatTimestamp={formatTimestamp} />
          </AdminSection>
        </>
      ) : null}

      {show('knowledge') ? (
        <AdminSection id="company-knowledge" title={t.admin.companyKnowledgeTitle} subtitle={t.admin.companyKnowledgeSubtitle}
          count={(knowledge?.documents || []).length} state={knowledge?.intake?.status ? `${t.admin.companyKnowledgeIntake}: ${knowledge.intake.status}` : ''}>
          {partError('knowledge')}
          <KnowledgePanel t={t} knowledge={knowledge} onChanged={reloadOverview} formatTimestamp={formatTimestamp} />
        </AdminSection>
      ) : null}

      {show('usage') ? (
        <>
          <AdminSection id="company-openrouter" title={t.admin.openrouterTitle} tone={overview?.openrouter?.data ? 'ok' : 'warning'}>
            <OpenRouterPanel openrouter={overview?.openrouter?.data} error={overview?.openrouter?.error} t={t} />
          </AdminSection>

          <AdminSection id="company-identities" title={n.companyIdentitiesTitle} subtitle={n.companyIdentitiesSubtitle}>
            {partError('gateway')}
            <div className="admin-table-shell"><div className="admin-table-scroll">
              <table className="admin-table admin-fixed-table">
                <colgroup><col style={{ width: '40%' }} /><col style={{ width: '30%' }} /><col style={{ width: '30%' }} /></colgroup>
                <thead><tr><th>{t.admin.aiGatewayIdentity}</th><th>{t.admin.companyTokensWindow}</th><th>{t.admin.companyBudget}</th></tr></thead>
                <tbody>
                  {[gateway, coreGateway].filter(Boolean).map((entry) => (
                    <tr key={entry.identity}><td className="admin-cell-clip" title={entry.identity}>{entry.identity || '-'}</td><td>{formatInteger(entry.tokens)}</td><td>{budgetOf(entry)}</td></tr>
                  ))}
                  {!gateway && !coreGateway ? <tr><td colSpan="3">{t.admin.gpuNoneYet}</td></tr> : null}
                </tbody>
              </table>
            </div></div>
            <div className="admin-inline-actions">
              <AdminLink tab="usage" section="identities">{t.admin.aiUsageTitle}</AdminLink>
              <AdminLink tab="engines">{t.admin.gpuIdentities}</AdminLink>
            </div>
          </AdminSection>

          <AdminSection id="company-usage" title={t.admin.companyUsageTitle} subtitle={t.admin.companyUsageSubtitle}>
            {partError('usage')}
            <UsageLedgerPanel usage={usage} t={t} note={<p className="text-xs text-text-muted">{t.admin.companyCostNote.replace('{identity}', gateway?.identity || '-')}</p>} />
          </AdminSection>
          {corePanel('usage', { sources: overview?.sources?.data })}
        </>
      ) : null}
    </>
  );

  if (embedded) {
    return <div className="admin-page-body">{pageBody}</div>;
  }

  return <main className="site-main admin-dashboard-main admin-page-body">{pageBody}</main>;
};

export default VeridionPaioneerPage;
